import { DEFAULT_TEXT_LINE_HEIGHT } from '../paint/text';
import {
  allLayers,
  copyLayerTree,
  findLayer,
  leafLayers,
  locateLayer,
  offsetLayerTree,
  removeLayerIn,
  replaceSiblings,
  updateLayerIn,
} from './tree';
import type {
  AdjustmentKind,
  AdjustmentLayer,
  BlendMode,
  EditorDocument,
  FillLayer,
  GroupLayer,
  ImageLayer,
  Layer,
  LayerBase,
  LayerMask,
  PaintLayer,
  ShapeKind,
  ShapeLayer,
  TextLayer,
} from './types';
import { SCHEMA_VERSION } from './types';

/**
 * Pure, immutable document operations.
 *
 * Every function returns a NEW document (or layer) and never mutates its inputs,
 * which is what makes undo/redo trivial and React rendering predictable.
 */

export function createId(): string {
  // crypto.randomUUID is available in all target browsers and in Node 19+.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export interface CreateDocumentOptions {
  width: number;
  height: number;
  name?: string;
  background?: string;
  layers?: Layer[];
}

export class DocumentSizeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocumentSizeError';
  }
}

export function createDocument(options: CreateDocumentOptions): EditorDocument {
  const { width, height } = options;
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    throw new DocumentSizeError(`Document size must be finite numbers, got ${width}x${height}`);
  }
  if (width < 1 || height < 1) {
    throw new DocumentSizeError(`Document size must be at least 1x1, got ${width}x${height}`);
  }
  if (!Number.isInteger(width) || !Number.isInteger(height)) {
    throw new DocumentSizeError(`Document size must be whole pixels, got ${width}x${height}`);
  }
  const now = new Date().toISOString();
  return {
    schemaVersion: SCHEMA_VERSION,
    id: createId(),
    name: options.name ?? 'Untitled',
    width,
    height,
    background: options.background ?? '#ffffff',
    layers: options.layers ? [...options.layers] : [],
    createdAt: now,
    modifiedAt: now,
  };
}

/** Shallow-copy a layer with a partial patch. Keeps `type` narrowing intact. */
export function patchLayer<L extends Layer>(layer: L, patch: Partial<L>): L {
  return { ...layer, ...patch };
}

function withBase(name: string, type: Layer['type'], overrides: Partial<LayerBase> = {}) {
  const base: LayerBase = {
    id: createId(),
    name,
    type,
    visible: true,
    locked: false,
    opacity: 1,
    blendMode: 'normal',
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    clipping: false,
    mask: null,
    ...overrides,
  };
  return base;
}

/**
 * Overrides that must win over a factory's defaults, minus `type`, which the
 * factory owns. Spreading overrides *before* the defaults silently threw away
 * caller values — a text layer came back at the default font no matter what was
 * asked for.
 */
function callerFields<O extends { type?: string }>(overrides: O): O {
  const { type: _type, ...rest } = overrides;
  return rest as O;
}

export function createImageLayer(
  src: string,
  naturalWidth: number,
  naturalHeight: number,
  overrides: Partial<ImageLayer> = {},
): ImageLayer {
  return {
    ...withBase(overrides.name ?? 'Image', 'image', overrides),
    type: 'image',
    src,
    naturalWidth,
    naturalHeight,
    ...callerFields(overrides),
  } satisfies ImageLayer;
}

export function createPaintLayer(overrides: Partial<PaintLayer> = {}): PaintLayer {
  return {
    ...withBase(overrides.name ?? 'Paint', 'paint', overrides),
    type: 'paint',
    pixels: '',
    // Callers that know the raster they are about to assign pass the real
    // size; 1×1 is the safe floor for the rest (a blank layer, or a test
    // that only needs the fields to exist).
    naturalWidth: 1,
    naturalHeight: 1,
    ...callerFields(overrides),
  } satisfies PaintLayer;
}

export function createShapeLayer(overrides: Partial<ShapeLayer> = {}): ShapeLayer {
  const kind: ShapeKind = overrides.kind ?? 'rect';
  return {
    ...withBase(overrides.name ?? `Shape ${kind}`, 'shape', overrides),
    type: 'shape',
    kind,
    width: 120,
    height: 120,
    fill: '#4c8dff',
    stroke: null,
    strokeWidth: 2,
    rx: 0,
    ...callerFields(overrides),
  } satisfies ShapeLayer;
}

export function createTextLayer(overrides: Partial<TextLayer> = {}): TextLayer {
  return {
    ...withBase(overrides.name ?? 'Text', 'text', overrides),
    type: 'text',
    text: 'Text',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 32,
    fontWeight: 'normal',
    italic: false,
    underline: false,
    color: '#15171c',
    align: 'left',
    lineHeight: DEFAULT_TEXT_LINE_HEIGHT,
    maxWidth: null,
    ...callerFields(overrides),
  } satisfies TextLayer;
}

export function createFillLayer(color = '#ffffff', overrides: Partial<FillLayer> = {}): FillLayer {
  return {
    ...withBase(overrides.name ?? 'Fill', 'fill', overrides),
    type: 'fill',
    color,
    ...callerFields(overrides),
  } satisfies FillLayer;
}

export function createAdjustmentLayer(
  kind: AdjustmentKind,
  value = 0,
  overrides: Partial<AdjustmentLayer> = {},
): AdjustmentLayer {
  return {
    ...withBase(overrides.name ?? `Adjust ${kind}`, 'adjustment', {
      clipping: false,
      ...overrides,
    }),
    type: 'adjustment',
    kind,
    value,
    ...callerFields(overrides),
  } satisfies AdjustmentLayer;
}

/* -------------------------------------------------------------------------- */
/* Layer list operations (all pure)                                            */
/* -------------------------------------------------------------------------- */

function touch(doc: EditorDocument): EditorDocument {
  return { ...doc, modifiedAt: new Date().toISOString() };
}

/** Insert `layer` at `index` (clamped). Appends on top when index is omitted. */
export function addLayer(doc: EditorDocument, layer: Layer, index?: number): EditorDocument {
  const layers = [...doc.layers];
  const at = index === undefined ? layers.length : Math.max(0, Math.min(index, layers.length));
  layers.splice(at, 0, layer);
  return touch({ ...doc, layers });
}

export function removeLayer(doc: EditorDocument, id: string): EditorDocument {
  const layers = removeLayerIn(doc.layers, id);
  if (layers === doc.layers) return doc;
  return touch({ ...doc, layers });
}

export function getLayer(doc: EditorDocument, id: string): Layer | undefined {
  return findLayer(doc.layers, id);
}

/**
 * The index of a layer in the **top-level** stack.
 *
 * A layer inside a group is not in the top-level stack at all, so it has no
 * index here — it has a place in its group's list. Use `locateLayer` from
 * `core/model/tree.ts` when the distinction matters.
 */
export function indexOfLayer(doc: EditorDocument, id: string): number {
  return doc.layers.findIndex((l) => l.id === id);
}

export function updateLayer<L extends Layer>(
  doc: EditorDocument,
  id: string,
  patch: Partial<L>,
): EditorDocument {
  // Cast is safe: callers pass a patch typed for the same layer variant.
  const layers = updateLayerIn(doc.layers, id, patch as Partial<Layer>);
  if (layers === doc.layers) return doc;
  return touch({ ...doc, layers });
}

/**
 * Move a layer to a new position **among its siblings**.
 *
 * A layer inside a group moves inside that group; a layer at the top moves in
 * the top-level stack. Returns the document unchanged if the id is unknown or
 * the position is the same.
 */
export function moveLayer(doc: EditorDocument, id: string, toIndex: number): EditorDocument {
  const location = locateLayer(doc.layers, id);
  if (!location) return doc;
  const { siblings, index } = location;
  const to = Math.max(0, Math.min(toIndex, siblings.length - 1));
  if (index === to) return doc;
  const next = [...siblings];
  const [moved] = next.splice(index, 1);
  if (!moved) return doc;
  next.splice(to, 0, moved);
  return touch({ ...doc, layers: replaceSiblings(doc.layers, location, next) });
}

/**
 * Copy a layer — and everything inside it, if it is a group — and put the copy
 * directly above its source, in the same list.
 *
 * The copy is offset by a few pixels, so it does not land exactly on top of
 * the thing it was copied from. A group has no position of its own, so the
 * offset goes on its children instead.
 */
export function duplicateLayer(
  doc: EditorDocument,
  id: string,
  nameFor?: (name: string) => string,
): EditorDocument {
  const location = locateLayer(doc.layers, id);
  if (!location) return doc;
  const source = location.siblings[location.index];
  if (!source) return doc;
  const copy = copyLayerTree(source, createId) as Layer;
  const copyName = nameFor ? nameFor(source.name) : `${source.name} copy`;
  const named = { ...copy, name: copyName } as Layer;
  // The copy gets its own mask object: sharing one would make a later
  // `patchMask` on either layer reach into the other.
  const withOwnMask =
    named.type === 'group'
      ? (named as GroupLayer)
      : ({ ...named, mask: named.mask ? { ...named.mask } : null } as Layer);
  const positioned =
    named.type === 'group'
      ? offsetLayerTree(withOwnMask as GroupLayer, 16, 16)
      : ({ ...withOwnMask, x: named.x + 16, y: named.y + 16 } as Layer);
  const next = [...location.siblings];
  next.splice(location.index + 1, 0, positioned);
  return touch({ ...doc, layers: replaceSiblings(doc.layers, location, next) });
}

export function setLayerVisibility(
  doc: EditorDocument,
  id: string,
  visible: boolean,
): EditorDocument {
  return updateLayer(doc, id, { visible });
}

export function setLayerClipping(
  doc: EditorDocument,
  id: string,
  clipping: boolean,
): EditorDocument {
  return updateLayer(doc, id, { clipping });
}

export function setLayerBlendMode(doc: EditorDocument, id: string, blendMode: BlendMode) {
  return updateLayer(doc, id, { blendMode });
}

export function setLayerOpacity(doc: EditorDocument, id: string, opacity: number): EditorDocument {
  return updateLayer(doc, id, { opacity: Math.max(0, Math.min(1, opacity)) });
}

/** Rename a layer, trimming and falling back to the previous name if empty. */
export function renameLayer(doc: EditorDocument, id: string, name: string): EditorDocument {
  const trimmed = name.trim();
  if (!trimmed) return doc;
  return updateLayer(doc, id, { name: trimmed.slice(0, 64) });
}

export function countLayers(doc: EditorDocument): number {
  return leafLayers(doc.layers).length;
}

/* -------------------------------------------------------------------------- */
/* Memory accounting                                                          */
/* -------------------------------------------------------------------------- */

/** Two bytes per JS string character — the conservative reading of base64. */
const BYTES_PER_CHAR = 2;
/** Fixed cost per layer for the fields every layer carries (id, name, flags). */
const LAYER_FIELD_CHARS = 96;

/**
 * Approximate heap cost of one layer, in bytes.
 */
export function estimateLayerBytes(layer: Layer): number {
  return layerChars(layer) * BYTES_PER_CHAR;
}

/**
 * The characters a layer costs.
 *
 * A layer's bulk is its base64 PNG (`src`, `pixels`, and a mask's `src`), so
 * those strings dominate. They are counted at two bytes per character: a JS
 * string is addressable as UTF-16, which makes this an upper bound rather than
 * a guess. Layers described by parameters — text, fill, shape — cost their own
 * fields, because those parameters are literally what is retained. A group
 * costs its children, because that is where its pixels are.
 */
function layerChars(layer: Layer): number {
  let chars = layer.name.length + LAYER_FIELD_CHARS;
  switch (layer.type) {
    case 'image':
      chars += layer.src.length;
      break;
    case 'paint':
      chars += layer.pixels.length;
      break;
    case 'text':
      chars += layer.text.length;
      break;
    case 'shape':
      chars += (layer.fill?.length ?? 0) + (layer.stroke?.length ?? 0);
      break;
    case 'fill':
      chars += layer.color.length;
      break;
    case 'adjustment':
      break;
    case 'group':
      chars += layer.children.reduce((total, child) => total + layerChars(child), 0);
      break;
  }
  if (layer.mask) chars += layer.mask.src.length;
  return chars;
}

/** Approximate heap cost of a whole document snapshot, in bytes. */
export function estimateDocumentBytes(doc: EditorDocument): number {
  return doc.layers.reduce((total, layer) => total + estimateLayerBytes(layer), 0);
}

/**
 * Bytes a new snapshot adds to the ones already kept.
 *
 * History stores whole documents, but the model is immutable: a commit copies
 * the document and the one layer it touched, and *shares* every other layer
 * object with the snapshot before it. Charging each snapshot the whole document
 * would therefore over-count by the length of the history, and a 4096px file
 * would lose its undo steps after eight strokes. So charge only the layers
 * whose object is new.
 *
 * Approximate by design: once eviction starts, a layer first counted by the
 * entry being dropped may still be shared by newer ones, so the tally can read
 * low by up to one document's worth. It never reads low by more than that.
 */
export function marginalLayerBytes(previous: EditorDocument | null, next: EditorDocument): number {
  if (!previous) return estimateDocumentBytes(next);
  const retained = new Set<Layer>(allLayers(previous.layers));
  let bytes = 0;
  for (const layer of allLayers(next.layers)) {
    if (retained.has(layer)) continue;
    bytes += estimateLayerBytes(layer);
  }
  return bytes;
}

/* -------------------------------------------------------------------------- */
/* Masks                                                                      */
/* -------------------------------------------------------------------------- */

/** Attach (or replace) a layer's mask. */
export function setMask(doc: EditorDocument, id: string, mask: LayerMask | null): EditorDocument {
  return updateLayer(doc, id, { mask });
}

/** Change mask flags or pixels without replacing the whole mask. */
export function patchMask(
  doc: EditorDocument,
  id: string,
  patch: Partial<LayerMask>,
): EditorDocument {
  const layer = getLayer(doc, id);
  if (!layer?.mask) return doc;
  return updateLayer(doc, id, { mask: { ...layer.mask, ...patch } });
}

export function removeMask(doc: EditorDocument, id: string): EditorDocument {
  const layer = getLayer(doc, id);
  if (!layer?.mask) return doc;
  return updateLayer(doc, id, { mask: null });
}

export function visibleLayerCount(doc: EditorDocument): number {
  return leafLayers(doc.layers).filter((l) => l.visible).length;
}
